// NotificationBell.jsx — SB-052 phase 1. The AppShell bell: unread badge +
// a dropdown of recent notifications. Rows arrive STRUCTURED (kind/entityNo/
// note); the text renders here from i18n keys, so it follows the UI language
// (master principle 4). Free-text notes append verbatim in their original
// language. Clicking a row marks it read and jumps to its surface — except
// terminal kinds whose item no surface can still show (review batch 48).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { useLang, useT } from '../i18n/index.mjs';
// 跳转判定搬去了 components/notifTarget.mjs（第一百零九批 D）：`.jsx` 不进单测
// （SB-042），而本批要往里加一条**带副作用风险**的分支——站内信的通知若跳进正文，
// "点一下铃铛"就成了"已读"（对方收到回执、撤回窗口永久关死），而用户以为自己只是
// 看了眼提醒。那条分支必须被测试钉住，所以判定不能留在这里。
import { targetOf } from './notifTarget.mjs';
// SB-159（第三百八十二批）：「加载更多」与「另有 N 条未读」的判定（纯函数，有单测）。
import {
  BELL_PAGE_LIMIT, loadMoreLabelKey, mergeNotifPages, showLoadMore, unreadOutside, unreadOutsideKey,
} from './notifPaging.mjs';

const POLL_MS = 60_000;

function kindLabel(t, n) {
  const k = `notif.${n.kind}`;
  const s = t(k, { no: n.entityNo });
  return s === k ? `${n.kind} · ${n.entityNo}` : s;   // raw-kind fallback
}

export default function NotificationBell() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState(null);   // null=loading, 'error'=fetch failed
  const [busy, setBusy] = useState(false);
  // SB-159（第三百八十二批）：最后一次取回那一页的 {hasMore, nextCursor}——「还有没有更多」以
  // **服务端说的**为准，不按行数猜；取下一页失败时按钮留在原地并出声（取不到 ≠ 没有了）。
  const [page, setPage] = useState(null);
  const [more, setMore] = useState({ loading: false, err: false });

  const refreshCount = useCallback(() => {
    api.notifications({ unread: true, limit: 1 })
      .then((r) => setUnread(r?.unreadCount ?? 0))
      .catch(() => { /* badge keeps its last value; next poll retries */ });
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(timer);
  }, [refreshCount]);

  const open = (e) => {
    setAnchorEl(e.currentTarget);
    setRows(null);
    setPage(null);
    setMore({ loading: false, err: false });
    api.notifications({ limit: BELL_PAGE_LIMIT })
      .then((r) => {
        setRows(Array.isArray(r?.notifications) ? r.notifications : []);
        setUnread(r?.unreadCount ?? 0);
        setPage(r);
      })
      // Review batch 48: a fetch failure must not masquerade as the
      // authoritative empty state next to a non-zero badge.
      .catch(() => setRows('error'));
  };

  // 下一页：沿 before = 上一页的 nextCursor 往旧翻，追加并按 id 去重。
  const loadMore = async () => {
    if (more.loading || !showLoadMore(page)) return;
    setMore({ loading: true, err: false });
    try {
      const r = await api.notifications({ limit: BELL_PAGE_LIMIT, before: page.nextCursor });
      setRows((prev) => mergeNotifPages(prev, r));
      setUnread(r?.unreadCount ?? unread);
      setPage(r);
      setMore({ loading: false, err: false });
    } catch {
      setMore({ loading: false, err: true });   // 按钮留着：再按一次就是重试
    }
  };
  const outside = Array.isArray(rows) ? unreadOutside(rows, unread) : 0;

  const onRow = async (n) => {
    const target = targetOf(n);
    if (target) setAnchorEl(null);
    if (!n.readAtMs) {
      try {
        await api.markNotificationRead(n.id);
        setRows((r) =>
          Array.isArray(r)
            ? r.map((x) => (x.id === n.id ? { ...x, readAtMs: Date.now() } : x))
            : r,
        );
      } catch {
        /* stale row — the next poll corrects */
      }
      refreshCount();
    }
    if (target) navigate(target);
  };

  const onReadAll = async () => {
    setBusy(true);
    try {
      await api.markAllNotificationsRead();
      setUnread(0);
      setRows((r) =>
        Array.isArray(r) ? r.map((n) => ({ ...n, readAtMs: n.readAtMs ?? Date.now() })) : r,
      );
    } catch {
      /* next poll corrects */
    } finally {
      setBusy(false);
    }
  };

  const fmt = (ms) => {
    // toLocaleString on an Invalid Date RETURNS "Invalid Date" (never throws),
    // so the guard must be explicit (review batch 48).
    if (!Number.isFinite(Number(ms))) return '';
    return new Date(Number(ms)).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <>
      {/* Text label button (matches the adjacent language toggle) — the repo
          convention is monochrome text glyphs/labels, no color emoji. The
          aria-label carries the count so screen readers hear it too. */}
      <Button
        color="inherit"
        onClick={open}
        aria-label={unread > 0 ? t('notif.aria.count', { n: unread }) : t('notif.aria')}
      >
        <Badge badgeContent={unread} color="error" max={99}>
          <Box component="span" sx={{ px: 0.25 }}>{t('notif.label')}</Box>
        </Badge>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '95vw' } } }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Typography variant="subtitle2">{t('notif.title')}</Typography>
        </MenuItem>
        {/* A real MenuItem so keyboard navigation reaches it (review batch 48:
            a Button inside a plain Box is skipped by the menu's roving focus
            and Tab closes the menu). */}
        <MenuItem disabled={busy || unread === 0} onClick={onReadAll}>
          <Typography variant="body2" color="primary">{t('notif.readAll')}</Typography>
        </MenuItem>
        <Divider />
        {rows === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : rows === 'error' ? (
          <Typography variant="body2" color="error" sx={{ px: 2, py: 2 }}>
            {t('notif.error')}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
            {t('notif.empty')}
          </Typography>
        ) : (
          rows.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => onRow(n)}
              sx={{ whiteSpace: 'normal', alignItems: 'flex-start',
                    opacity: n.readAtMs ? 0.55 : 1 }}
            >
              <ListItemText
                primary={kindLabel(t, n)}
                secondary={
                  <>
                    {n.note ? (
                      <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                        {n.note}
                      </Typography>
                    ) : null}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {fmt(n.createdAtMs)}
                    </Typography>
                  </>
                }
                slotProps={{
                  primary: { variant: 'body2', fontWeight: n.readAtMs ? 400 : 600 },
                }}
              />
            </MenuItem>
          ))
        )}
        {/* SB-159：角标说 40、列表 15 行时，那 25 条在哪儿必须说出来——不看 hasMore，
            服务端说谎（hasMore=false 而未读还有）时这一行照样出现。复审（N6）：说法随按钮在不在换
            （unreadOutsideKey）——没有「加载更多」时叫人往前翻是指错方向。 */}
        {Array.isArray(rows) && outside > 0 ? (
          <MenuItem disabled sx={{ opacity: 1, whiteSpace: 'normal' }}>
            <Typography variant="caption" color="text.secondary" data-notif-unread-outside={outside}>
              {t(unreadOutsideKey(page), { n: outside })}
            </Typography>
          </MenuItem>
        ) : null}
        {/* 真 MenuItem（review 48 那条：键盘的 roving focus 要够得着它），且**只在服务端说
            hasMore 时**出现；取下一页失败时它留在原地并出声。 */}
        {Array.isArray(rows) && showLoadMore(page) ? (
          <MenuItem onClick={loadMore} disabled={more.loading} data-notif-load-more>
            <Box>
              <Typography variant="body2" color="primary">{t(loadMoreLabelKey(more))}</Typography>
              {more.err ? (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  {t('notif.loadMoreError')}
                </Typography>
              ) : null}
            </Box>
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
